import { useEffect, useReducer, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import type { GigWorkerClient } from '../../../infrastructure/GigWorkerClient';
import type { GigOrderPageSnapshotView } from '../../../models/GigWorker';
import {
  FRAME_INTERVAL_MS,
  formatCapturedAt,
  frameLabel,
  initialSnapshotPlayerState,
  snapshotReducer,
} from './snapshotPlayer';

interface GigOrderPageSnapshotsProps {
  client: GigWorkerClient;
  orderId: string;
}

/** Replays the Coupang page screenshots a worker captured for an order as a stepped/auto-playing slideshow. */
export default function GigOrderPageSnapshots({ client, orderId }: GigOrderPageSnapshotsProps) {
  const requestId = useRef(0);
  const [snapshots, setSnapshots] = useState<GigOrderPageSnapshotView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [player, dispatch] = useReducer(snapshotReducer, initialSnapshotPlayerState(0));

  useEffect(() => {
    const currentRequest = ++requestId.current;
    setIsLoading(true);
    setError(null);
    client
      .getOrderPageSnapshots(orderId)
      .then((result) => {
        if (currentRequest !== requestId.current) {
          return;
        }
        setSnapshots(result);
        dispatch({ type: 'reset', count: result.length });
      })
      .catch((loadError: unknown) => {
        if (currentRequest !== requestId.current) {
          return;
        }
        setError(loadError instanceof Error ? loadError.message : 'Failed to load page snapshots');
      })
      .finally(() => {
        if (currentRequest === requestId.current) {
          setIsLoading(false);
        }
      });
  }, [client, orderId]);

  // Auto-advance one frame per interval while playing.
  useEffect(() => {
    if (!player.isPlaying) {
      return;
    }
    const timer = setInterval(() => dispatch({ type: 'tick' }), FRAME_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [player.isPlaying]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-3">
        <Spinner animation="border" size="sm" role="status" aria-label="Loading page snapshots" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (snapshots.length === 0) {
    return <p className="text-muted small mb-0">No page snapshots captured.</p>;
  }

  const current = snapshots[player.index]!;
  const label = frameLabel(player.index, player.count);

  return (
    <div>
      <div className="bg-light border rounded d-flex justify-content-center mb-2" style={{ maxHeight: 420, overflow: 'auto' }}>
        <img
          src={current.imageUrl}
          alt={`Page snapshot ${player.index + 1} of ${player.count}`}
          className="img-fluid"
          style={{ maxHeight: 420 }}
        />
      </div>

      <div className="small text-muted mb-2">
        <div className="text-truncate">
          <a href={current.capturedUrl} target="_blank" rel="noreferrer" className="text-primary text-decoration-none">
            {current.capturedUrl}
          </a>
        </div>
        <div>
          {current.title ? `${current.title} · ` : ''}
          {formatCapturedAt(current.capturedAt)}
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => dispatch({ type: 'prev' })}
          disabled={player.index === 0}
          aria-label="Previous frame"
        >
          ‹
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => dispatch({ type: 'toggle' })}
          disabled={player.count <= 1}
          aria-label={player.isPlaying ? 'Pause' : 'Play'}
        >
          {player.isPlaying ? 'Pause' : 'Play'}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => dispatch({ type: 'next' })}
          disabled={player.index >= player.count - 1}
          aria-label="Next frame"
        >
          ›
        </Button>
        <span className="small text-muted font-monospace ms-1">{label}</span>
      </div>

      <Form.Range
        min={0}
        max={player.count - 1}
        value={player.index}
        onChange={(event) => dispatch({ type: 'seek', index: Number(event.target.value) })}
        aria-label="Seek frames"
      />
    </div>
  );
}
